import { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import DateProvider from './context/DateProvider';
import { KpisSelectionProvider } from './context/KpisSelectionProvider';
import { PortfoliosFilterProvider } from './context/PortfoliosFilterProvider';

import PrivateApp from './routes/PrivateApp';
import PrivateWizard from './routes/PrivateWizard';
import { PATHS } from './routes/paths';
import { RequireRole } from './Auth/rbac';
import { RequireFeature } from './entitlements';
import { RequireTrendSignals } from './routes/trendSignals/RequireTrendSignals';

const Portfolio = lazy(() => import('./routes/Portfolio'));
const Portfolios = lazy(() => import('./routes/Portfolios'));
const Attributions = lazy(() => import('./routes/Attributions'));
const AttributionRunDetails = lazy(() => import('./routes/AttributionRunDetails'));
const OnboardingGuide = lazy(() => import('./routes/OnboardingGuide'));
const Content = lazy(() => import('./routes/Content'));
const DashboardSelection = lazy(() => import('./routes/DashboardSelection'));
const KPI = lazy(() => import('./routes/KPI'));
const KPIs = lazy(() => import('./routes/KPIs'));
const KPIsCompareRedirect = lazy(() => import('./routes/KPIsCompareRedirect'));
const Reports = lazy(() => import('./routes/Reports'));
const Report = lazy(() => import('./routes/Report'));
const Settings = lazy(() => import('./routes/Settings'));
const TabletDemo = lazy(() => import('./routes/Cockpit'));
const KfzCockpit = lazy(() => import('./routes/KfzCockpit'));
const TrendSignals = lazy(() => import('./routes/trendSignals/TrendSignalsPage'));
const Oauth = lazy(() => import('./routes/Oauth'));
const Unauthorized = lazy(() => import('./Auth/Unauthorized'));
const Onboarding = lazy(() => import('./routes/onboard/Onboarding'));
const OnboardingRedirect = lazy(() => import('./routes/onboard/OnboardingRedirect'));

const Routes = () => (
  <Switch>
    <Route path={PATHS.AUTH.SIGN_UP} component={OnboardingRedirect} />
    <Route
      path={PATHS.WIZARD.ONBOARD}
      render={(props) => (
        <PrivateWizard {...props}>
          <Suspense fallback={<div />}>
            <Switch>
              <Route exact path={PATHS.WIZARD.ONBOARD} component={Onboarding} />
              <Route
                exact
                path={PATHS.WIZARD.ONBOARD_INVITE}
                component={() => <Redirect to={PATHS.WIZARD.ONBOARD} />}
              />
              <Route render={() => <div>Nothing matches in the onboard</div>} />
            </Switch>
          </Suspense>
        </PrivateWizard>
      )}
    />

    <Route path={PATHS.OAUTH.HOME} component={Oauth} />

    <Route
      path={PATHS.APP.TABLET_DEMO}
      render={() => (
        <Suspense fallback={<div />}>
          <DateProvider>
            <TabletDemo />
          </DateProvider>
        </Suspense>
      )}
    />

    <Route
      path={PATHS.APP.HOME}
      render={(props) => (
        <PrivateApp {...props}>
          <Suspense fallback={<div />}>
            <Switch>
              <Route exact path={PATHS.APP.HOME} component={OnboardingRedirect} />
              <Route exact path={PATHS.APP.KFZ_COCKPIT} component={KfzCockpit} />
              <Route exact path={PATHS.APP.ONBOARD_GUIDE} component={OnboardingGuide} />
              <Route exact path={PATHS.APP.HOME_KPI_PICKER} component={DashboardSelection} />
              <Route
                path={PATHS.APP.KPIS}
                render={() => (
                  // Nexoya Premium only, and gated OUTSIDE the inner Switch so every /kpis
                  // subpath is covered. Redirect rather than the upsell teaser, as with
                  // Attributions below: standalone metrics are not part of the Starter offer.
                  <RequireFeature feature="metrics.view" fallback={<Redirect to={PATHS.APP.PORTFOLIOS} />}>
                    <KpisSelectionProvider>
                      <Switch>
                        <Route exact path={PATHS.APP.KPIS} component={KPIs} />
                        <Route path={PATHS.APP.KPI} component={KPI} />
                        <Route path={PATHS.APP.KPIS_COMPARE} component={KPIsCompareRedirect} />
                      </Switch>
                    </KpisSelectionProvider>
                  </RequireFeature>
                )}
              />
              <Route
                path={PATHS.APP.PORTFOLIOS}
                render={() => (
                  <>
                    <PortfoliosFilterProvider>
                      <Suspense fallback={<div />}>
                        <Switch>
                          <Route exact path={PATHS.APP.PORTFOLIOS} component={Portfolios} />
                          <Route exact path={PATHS.APP.PORTFOLIO} component={Portfolio} />
                        </Switch>
                      </Suspense>
                    </PortfoliosFilterProvider>
                  </>
                )}
              />
              <Route
                path={PATHS.APP.ATTRIBUTIONS}
                render={() => (
                  // Attribution is Full-only. The sidebar entry was already support-only, but
                  // the URL itself was mounted with no guard at all, so anyone who typed it
                  // got the page. The fallback is deliberately NOT the upsell teaser that
                  // Optimize and Validation use: those are sold to Nexoya Starter teams,
                  // attribution is not part of the offer, so it reads as absent rather than
                  // as an invitation.
                  <RequireFeature feature="attribution.view" fallback={<Redirect to={PATHS.APP.PORTFOLIOS} />}>
                    <PortfoliosFilterProvider>
                      <Suspense fallback={<div />}>
                        <Switch>
                          <Route exact path={PATHS.APP.ATTRIBUTIONS} component={Attributions} />
                          <Route exact path={PATHS.APP.ATTRIBUTION} component={AttributionRunDetails} />
                        </Switch>
                      </Suspense>
                    </PortfoliosFilterProvider>
                  </RequireFeature>
                )}
              />
              <Route
                path={PATHS.APP.REPORTS}
                render={() => (
                  <>
                    <Suspense fallback={<div />}>
                      <Switch>
                        <Route exact path={PATHS.APP.REPORTS} component={Reports} />
                        <Route exact path={PATHS.APP.REPORT} component={Report} />
                      </Switch>
                    </Suspense>
                  </>
                )}
              />
              <Route
                path={PATHS.APP.SETTINGS}
                render={() => (
                  <RequireRole permission="team.settings.view">
                    <Settings />
                  </RequireRole>
                )}
              />
              <Route
                path={[PATHS.APP.TREND_SIGNAL, PATHS.APP.TREND_SIGNALS]}
                render={() => (
                  // Suspense inside the gate: under the outer boundary the gate's team query remounted
                  // in a loop while the page loaded and froze the tab.
                  <RequireTrendSignals>
                    <Suspense fallback={<div />}>
                      <TrendSignals />
                    </Suspense>
                  </RequireTrendSignals>
                )}
              />
              <Route path={PATHS.APP.CONTENT} component={Content} />
              <Redirect to={PATHS.APP.HOME} />
            </Switch>
          </Suspense>
        </PrivateApp>
      )}
    />
    <Route path={PATHS.AUTH.UNAUTHORIZED} component={Unauthorized} />
  </Switch>
);

export default Routes;
